import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';

const NotificationPanel = ({ userId, maxItems = 5, showFilters = true }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  // Validate if userId is a valid UUID format
  const isValidUUID = (id) => {
    if (!id) return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex?.test(id);
  };

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to real-time notifications
    const channel = supabase?.channel('notification_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_history'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setNotifications(prev => [payload?.new, ...prev]);
            if (!payload?.new?.is_read) {
              setUnreadCount(prev => prev + 1);
            }
          } else if (payload?.eventType === 'UPDATE') {
            setNotifications(prev => 
              prev?.map(n => n?.id === payload?.new?.id ? payload?.new : n)
            );
            if (payload?.old?.is_read === false && payload?.new?.is_read === true) {
              setUnreadCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [userId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('notification_history')?.select('*')?.order('created_at', { ascending: false })?.limit(maxItems);

      // Only filter by user_id if it's a valid UUID
      if (userId && isValidUUID(userId)) {
        query = query?.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n?.is_read)?.length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase?.from('notification_history')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error?.message);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications?.filter(n => !n?.is_read)?.map(n => n?.id);
      
      if (unreadIds?.length === 0) return;

      const { error } = await supabase?.from('notification_history')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.in('id', unreadIds);

      if (error) throw error;

      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error?.message);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      'compliance_violation': { icon: 'AlertTriangle', color: 'text-red-500' },
      'emergency_procedure': { icon: 'AlertCircle', color: 'text-orange-500' },
      'booking_change': { icon: 'Calendar', color: 'text-blue-500' },
      'approval_status': { icon: 'CheckCircle', color: 'text-green-500' },
      'travel_request': { icon: 'Plane', color: 'text-purple-500' },
      'travel_escalation': { icon: 'ArrowUp', color: 'text-red-600' },
      'policy_update': { icon: 'FileText', color: 'text-gray-500' },
      'default': { icon: 'Bell', color: 'text-gray-400' }
    };

    return iconMap?.[type] || iconMap?.default;
  };

  const getPriorityBadge = (metadata) => {
    const priority = metadata?.priority || 'normal';
    const badgeStyles = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badgeStyles?.[priority]}`}>
        {priority?.charAt(0)?.toUpperCase() + priority?.slice(1)}
      </span>
    );
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications?.filter(n => n?.notification_type === filter);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Bell" className="w-5 h-5 text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-900">Live Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            Mark all read
          </button>
        )}
      </div>
      {/* Filters */}
      {showFilters && (
        <div className="px-4 py-2 border-b border-gray-100 flex gap-2 overflow-x-auto">
          {[
            { value: 'all', label: 'All' },
            { value: 'travel_escalation', label: 'Escalations' },
            { value: 'compliance_violation', label: 'Compliance' },
            { value: 'emergency_procedure', label: 'Emergency' },
            { value: 'booking_change', label: 'Bookings' },
            { value: 'approval_status', label: 'Approvals' }
          ]?.map((filterOption) => (
            <button
              key={filterOption?.value}
              onClick={() => setFilter(filterOption?.value)}
              className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === filterOption?.value
                  ? 'bg-blue-100 text-blue-700' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filterOption?.label}
            </button>
          ))}
        </div>
      )}
      {/* Notifications List */}
      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center">
            <Icon name="Loader" className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading notifications...</p>
          </div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Icon name="Bell" className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No notifications</p>
          </div>
        ) : (
          filteredNotifications?.map((notification) => {
            const { icon, color } = getNotificationIcon(notification?.notification_type);
            return (
              <div
                key={notification?.id}
                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification?.is_read ? 'bg-blue-50' : ''
                }`}
                onClick={() => !notification?.is_read && markAsRead(notification?.id)}
              >
                <div className="flex gap-3">
                  <div className={`flex-shrink-0 ${color}`}>
                    <Icon name={icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {notification?.title}
                      </h4>
                      {notification?.metadata && getPriorityBadge(notification?.metadata)}
                    </div>
                    <p className="text-xs text-gray-600 mb-1 line-clamp-2">
                      {notification?.message}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {formatTimeAgo(notification?.created_at)}
                      </span>
                      {!notification?.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {/* Footer */}
      {filteredNotifications?.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 text-center">
          <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;